import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OverviewCard from "../common/OverviewCard/OverviewCard";
import AboutCard from "../common/AboutCard/AboutCard";
import ReviewsCard from "../common/ReviewsCard/ReviewsCard";
import ProformanceCard from "../common/ProformanceCard/ProformanceCard";
import PropertiesCard from "../common/PropertiesCard/PropertiesCard";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import AgencyConnectCard from "../common/AgencyConnectCard/AgencyConnectCard";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";
import HeaderImg from "../../assets/findAgent/headerImg.webp";
import VideoImg from "../../assets/findAgent/videoImg1.png";
import { MdOutlineEmail } from "react-icons/md";
import { FaStar } from "react-icons/fa";
import { OurTopMarkets } from "../FindAgent/OurTopMarkets";
import ShareModal from "../common/AgencyConnectCard/ShareModal";
import AgencyNewContactCard from "../common/AgentContactCard/AgencyNewContactCard";
import { IoBagOutline } from "react-icons/io5";
import { IoMdCall } from "react-icons/io";
import AgencyGroupNetwork from "./AgencyGroupNetwork";
import { getAgencySocialLinks } from "../common/utils/agencySocialLinks";
import {
  formatAgencyReviewAverage,
  getAgencyReviewStats,
} from "../../utils/agencyReviewStats";
import { useRecentlySoldProperties } from "../../Hooks/useRecentlySoldProperties/useRecentlySoldProperties";

const PROFILE_SECTIONS = [
  "Overview",
  "Properties",
  "Performance",
  "About",
  "Reviews",
  "Contact",
];

const AgencyProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [AgencyData, setAgencyData] = useState({});
  const [MarketPerformanceData, setMarketPerformanceData] = useState({});
  const [AgentsInfo, setAgentsInfo] = useState({});
  const [isAgencyLoading, setIsAgencyLoading] = useState(true);

  const [showShareModal, setShowShareModal] = useState(false);

  const [property, setProperty] = useState({});

  const { average: totalaverage, totalCount: reviewlength } = useMemo(() => {
    const fromAgentsList = getAgencyReviewStats(AgentsInfo);
    if (fromAgentsList.totalCount > 0) return fromAgentsList;
    return getAgencyReviewStats(
      AgencyData?.[0]?.agents,
      AgencyData?.[0]?.agencyReviews,
    );
  }, [AgentsInfo, AgencyData]);

  const agencySocialLinks = useMemo(
    () => getAgencySocialLinks(AgencyData?.[0]),
    [AgencyData],
  );

  const { data: recentlySoldProperties } = useRecentlySoldProperties(50);

  useEffect(() => {
    setIsAgencyLoading(true);
    Promise.all([GetAgentData(id), GetAllAgentData(id)]).finally(() => {
      setIsAgencyLoading(false);
    });
  }, [id]);

  const GetAgentData = (id) => {
    return axiosInstanceAuth
      .post(`agency/ViewProfile_U`, {
        id: id,
      })
      .then((res) => {
        const mydata = res?.data?.data;
        if (res?.data?.status) {
          setAgencyData(mydata);
          setProperty(res?.data?.data?.[0]?.properties);
          setMarketPerformanceData(res?.data?.marketPerformance);
        } else {
        }
      })
      .catch((err) => {
        console.log("err --->", err);
      });
  };

  const GetAllAgentData = (id) => {
    return axiosInstanceAuth
      .post("Agency_Agent/viewAllAgentsOfAgency_U", {
        id: id,
      })
      .then((res) => {
        const mydata = res?.data?.data;
        if (res?.data?.status) {
          setAgentsInfo(mydata);
        } else {
        }
      })
      .catch((err) => {
        //console.log("err --->", err);
      });
  };

  const [isActive, setisActive] = useState("Overview");
  const isClickScrollingRef = useRef(false);
  const scrollSpyRafRef = useRef(null);

  const ActiveTab =
    "border-b-[#097FE6] text-[#097FE6] font-semibold ";
  const NormalTab =
    "grid place-items-start text-[#000929] text-sm md:text-md lg:text-base border border-b-2 border-transparent hover:border-b-[#097FE6] py-3 px-[18px] ease-in-out duration-700 cursor-pointer";

  const tabData = PROFILE_SECTIONS.map((title, index) => ({
    id: index + 1,
    title,
  }));

  const getStickyNavOffset = useCallback(() => {
    const nav = document.getElementById("navigation");
    return (nav?.offsetHeight ?? 52) + 12;
  }, []);

  const updateActiveSectionFromScroll = useCallback(() => {
    if (isClickScrollingRef.current) return;

    const offset = getStickyNavOffset();
    let activeSection = PROFILE_SECTIONS[0];

    for (const sectionId of PROFILE_SECTIONS) {
      const section = document.getElementById(sectionId);
      if (!section) continue;
      if (section.getBoundingClientRect().top <= offset) {
        activeSection = sectionId;
      }
    }

    setisActive((prev) => (prev === activeSection ? prev : activeSection));
  }, [getStickyNavOffset]);

  useEffect(() => {
    if (isAgencyLoading) return undefined;

    const onScroll = () => {
      if (scrollSpyRafRef.current) return;
      scrollSpyRafRef.current = requestAnimationFrame(() => {
        scrollSpyRafRef.current = null;
        updateActiveSectionFromScroll();
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateActiveSectionFromScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (scrollSpyRafRef.current) {
        cancelAnimationFrame(scrollSpyRafRef.current);
      }
    };
  }, [isAgencyLoading, updateActiveSectionFromScroll]);

  const handleClick = (sectionId) => {
    setisActive(sectionId);
    isClickScrollingRef.current = true;

    const section = document.getElementById(sectionId);
    if (section) {
      const top =
        section.getBoundingClientRect().top +
        window.scrollY -
        getStickyNavOffset();
      window.scrollTo({ top, behavior: "smooth" });
    }

    window.setTimeout(() => {
      isClickScrollingRef.current = false;
      updateActiveSectionFromScroll();
    }, 700);
  };

  const ReviewTags = [
    "Professional",
    "Great communicator",
    "Genuine",
  ];

  if (isAgencyLoading) {
    return (
      <div className="bg-[#F3F6F9] animate-pulse">
        <div className="agency-profile-header">
          <div className="bg-gray-200 px-6 py-4 flex justify-center h-12" />
          <div className="w-full h-[200px] md:h-[320px] bg-gray-200" />
        </div>
        <div className="bg-white px-4 lg:px-0">
          <div className="container mx-auto bg-white py-12">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-10">
              <div className="w-full md:w-2/3">
                <div className="h-8 bg-gray-200 rounded w-72 mb-3" />
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-80 mb-6" />
                <div className="h-10 bg-gray-200 rounded-full w-72" />
              </div>
              <div className="w-full md:w-[320px]">
                <div className="h-14 bg-gray-200 rounded-full w-full mb-5" />
                <div className="flex gap-3">
                  <div className="h-14 bg-gray-200 rounded-full flex-1" />
                  <div className="h-14 bg-gray-200 rounded-full flex-1" />
                </div>
              </div>
            </div>
            <div className="mt-8 h-12 bg-gray-200 rounded-full w-full" />
            <div className="mt-10 grid lg:grid-cols-4 gap-10">
              <div className="lg:col-span-3 space-y-8">
                <div className="h-36 bg-gray-200 rounded-2xl" />
                <div className="h-36 bg-gray-200 rounded-2xl" />
                <div className="h-36 bg-gray-200 rounded-2xl" />
                <div className="h-36 bg-gray-200 rounded-2xl" />
              </div>
              <div className="hidden xl:block lg:col-span-1">
                <div className="h-[520px] bg-gray-200 rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F3F6F9]">
      <div className="agency-profile-header ">
        <div
          className={`px-6 py-4 flex justify-center ${AgencyData[0]?.primary_color ? "" : "bg-black"}`}
          style={
            AgencyData[0]?.primary_color
              ? { backgroundColor: AgencyData[0].primary_color }
              : undefined
          }
        >
          <img
            src={AgencyData[0]?.agencyLargeLogo || HeaderImg}
            alt="agency logo"
            className="h-8"
          />
        </div>
        <img
          src={AgencyData[0]?.heroImg || VideoImg}
          alt="Red white"
          className="w-full h-[200px] md:h-[400px] object-cover"
        />
      </div>
      <div className="bg-white px-4 lg:px-0">
        <div className="container mx-auto bg-white py-12">
          <div className="flex flex-col md:flex-row justify-between md:items-center">
            <div className="flex flex-col gap-1">
              <div className="text-[#000929] font-semibold text-2xl">
                {AgencyData[0]?.principal_name}
              </div>
              <div className="text-[#000929] font-normal text-base flex gap-[3px]">
                {" "}
                {AgencyData[0]?.street}, {AgencyData[0]?.state_region},{" "}
                {AgencyData[0]?.country}
              </div>
              <div className="flex lg:items-center flex-row md:flex-col lg:flex-row gap-1 text-sm lg:text-base text-[#000929] font-normal">
                <span className="flex gap-1 items-center">
                  {" "}
                  <FaStar className="text-[#C2C823] text-sm" />{" "}
                  {formatAgencyReviewAverage(totalaverage)}{" "}
                </span>
                <span className="">( {reviewlength} {reviewlength === 1 ? "review" : "reviews"} ) </span>
              </div>
              {/* <div className="flex lg:items-center flex-row md:flex-col lg:flex-row gap-1 text-sm lg:text-base text-[#000929] font-normal">
                <span className="flex gap-1 items-center">
                  <IoBagOutline />
                </span>
                <span className="text-sm">1 of multiple offices in Ray White Malan & Co agency group</span>
              </div> */}

              {agencySocialLinks.length > 0 && (
                <div className="flex items-center gap-3 mt-2">
                  {agencySocialLinks.map(({ key, label, url, Icon, colorClass }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${AgencyData[0]?.principal_name || "Agency"} on ${label}`}
                      title={label}
                      className={`inline-flex items-center justify-center transition-opacity hover:opacity-80 ${colorClass}`}
                    >
                      <Icon size={22} aria-hidden />
                    </a>
                  ))}
                </div>
              )}

              <div className="flex  items-center gap-2 mt-2">
                <div className="">
                  <AvatarGroup total={AgentsInfo?.length}>
                    {AgentsInfo?.length > 0 &&
                      AgentsInfo?.map((i, index) => (
                        <Avatar
                          key={index}
                          alt=""
                          src={i?.profileImg}
                          className="aspect-square rounded-full cursor-pointer"
                          onClick={() => navigate(`/agent-profile/${i?._id}`)}
                        />
                      ))}
                  </AvatarGroup>
                </div>
                <div className="text-[#726E75] font-light text-base">
                  {AgentsInfo?.length} people work here
                </div>
              </div>
              {/* <div className="flex flex-wrap justify-start items-center gap-2 mt-3">
                {ReviewTags?.length > 0 &&
                  ReviewTags?.map((i, index) => (
                    <div
                      key={index}
                      className="flex justify-center items-center bg-[#0009291A] rounded-full font-medium text-[#000929] text-xs  py-2 px-3"
                    >
                      {i}
                    </div>
                  ))}
              </div> */}
            </div>

            {/* button */}
            <div className="flex flex-col gap-4 mt-4 w-full md:max-w-[320px]">
              <button
                className="w-full flex justify-center items-center gap-2 text-base font-medium rounded-full bg-[#097FE6] py-3 px-2 cursor-pointer text-white text-center"
                onClick={() => {
                  handleClick("Contact");
                }}
              >
                <span>Request a free appraisal</span>
              </button>
              <div className="flex gap-3">
                <a
                  className="w-full flex justify-center items-center gap-2 text-base font-medium rounded-full border-[1.5px] border-[#EAEAEA] text-[#000929] py-3 px-8 cursor-pointer text- text-center"
                  href={`tel:${AgencyData[0]?.mobile_number}`}
                  rel="noopener noreferrer"
                >
                  <IoMdCall className="w-4 cursor-pointer" />
                  <span>Call Agency</span>
                </a>
              </div>


              <div className="flex gap-3">
                <button
                  className="w-full flex justify-center items-center gap-2 text-base font-medium rounded-full border-[1.5px] border-[#EAEAEA] text-[#000929] py-3 px-8 cursor-pointer text- text-center"
                  onClick={() => handleClick("Contact")}
                >
                  <MdOutlineEmail className="w-4 cursor-pointer" />
                  <span>Enquire</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="sticky top-0 z-40 bg-white border-b border-[#EAEAEA] shadow-sm">
        <div
          id="navigation"
          className="container overflow-x-scroll scrollBarHidden "
        >
          <div className="flex justify-start items-center border border-b-2 gap-2 border-transparent">
            {tabData.map((tab) => (
              <div
                key={tab.id}
                className={`${NormalTab} ${isActive === tab.title ? ActiveTab : ""
                  }`}
                onClick={() => {
                  handleClick(tab.title);
                  setisActive(tab.title);
                }}
              >
                {tab.title}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto grid lg:grid-cols-4">
        {/* ------ Left Part ------ */}
        <div className="flex flex-col col-span-3">
          {/* ------ Overview ------ */}
          <div id="Overview" className="mt-4 md:mt-6 scroll-mt-[72px]">
            <OverviewCard AgencyData={AgencyData} MarketPerformanceData={MarketPerformanceData} />
          </div>

          {/* <div id="Overview" className="mt-4 md:mt-6">
            <AgencyGroupNetwork property={property} />
          </div> */}

          {/* ------ Properties ------ */}
          <div id="Properties" className="mt-4 md:mt-8 scroll-mt-[72px]">
            {/* <AgentPropertiesCard property={property} AgencyData={AgencyData}/> */}
            {/* temporary comment the component */}
            <PropertiesCard
              property={property}
              AgencyData={AgencyData}
              MarketPerformanceData={MarketPerformanceData}
              agencyId={id}
              recentlySoldProperties={recentlySoldProperties}
            />
          </div>


          {/* <div className="mt-4 md:mt-8">
            <OurTopMarkets />
          </div> */}

          {/* ------ Performance ------ */}
          <div id="Performance" className="mt-4 md:mt-8 scroll-mt-[72px]">
            <ProformanceCard
              AgencyData={AgencyData}
              MarketPerformanceData={MarketPerformanceData}
            />
          </div>


          {/* <div className="xl:hidden mt-4 md:mt-8">
                <AgencyConnectCard />
              </div> */}

          {/* ------ About ------ */}
          <div id="About" className="mt-4 md:mt-8 scroll-mt-[72px]">
            <AboutCard AgentsInfo={AgentsInfo} AgencyData={AgencyData} />
          </div>

          {/* ------ Reviews ------ */}
          <div id="Reviews" className="mt-4 md:mt-8 scroll-mt-[72px]">
            <ReviewsCard
              totalaverage={totalaverage}
              reviewlength={reviewlength}
              AgentsInfo={AgentsInfo}
              AgencyData={AgencyData}
            />
          </div>

          {/* ------ Contact ------ */}
          <div id="Contact" className="mt-4 md:mt-8 mb-8 scroll-mt-[72px]">
            <AgencyNewContactCard
              AgencyData={AgencyData[0]}
              reviewlength={reviewlength}
              totalaverage={totalaverage}
            />
          </div>
        </div>
        {/* ------ Right Part ------ */}
        <div className="hidden sm:block mt-3 col-span-1">
          <AgencyConnectCard
            totalaverage={totalaverage}
            reviewlength={reviewlength}
            handleClick={handleClick}
            AgencyData={AgencyData}
          />
        </div>
      </div>

      {showShareModal && (
        <ShareModal
          agency={AgencyData[0]}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default AgencyProfile;
